<template>
  <main-input-dialog
    ref="table-buttons-copy-objects-dialog"
    :is-visible.sync="computedIsVisible"
    :loading="computedIsLoading"
    :title-translation="dialogTitle || $i18n.t('sidebar.copyObjects.title')"
    :agree-text="agreeText"
    dialog-width="35%"
    @agree="copyObjectsHandler"
    :inputs="copyObjectsGeometryInputs"
    disable-default-form-bottom-padding
    :custom-is-valid="customIsValid"
    :formCols="formCols"
  >
    <template #layerId.input="data">
      <v-col cols="6">
        <data-input v-model="targetLayerId" v-bind="data" />
      </v-col>
    </template>
    <template #isMapAttributes.input="data">
      <v-col cols="6" class="pt-3 pb-2">
        <data-input v-model="isMapAttributes" v-bind="data">
          <template #label>
            <span :class="{ 'font--text': targetLayerId }">{{ data.label }}</span>
          </template>
        </data-input>
      </v-col>
    </template>
    <template v-for="(attribute, idx) of targetLayerAttributesEditable" #[`${attribute.attribute}.input`]="data">
      <v-col cols="6" :key="idx">
        <data-input v-model="mapAttributes[attribute.attribute]" v-bind="data" />
      </v-col>
    </template>
    <template v-for="(value, key) in $scopedSlots" #[key]="scope">
      <slot :name="key" v-bind="scope" />
    </template>
  </main-input-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import DataInput from '@/components/DataInput';
import MainInputDialog from '@/components/MainInputDialog';

export default {
  name: 'CopyFeaturesDialog',
  components: { DataInput, MainInputDialog },
  mixins: [prepareAttributesFeatureCardMixin],
  props: {
    name: {
      // Used for identification to save 'targetLayerId' in local storage
      // for each tool separately
      // Local storage is not used if not provided
      type: String,
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    inputLayerId: {
      type: [Number, String],
    },
    inputFeaturesIds: {
      type: Array,
      default: () => {
        return [];
      },
    },
    geojson: {
      type: Object,
    },
    /**
     * Required for mapping if geojson provided instead of layer id.
     * Example of attribute's structure in array:
     * {
     *  attribute: <attribute_name>,
     *  label: <attribute_label>,
     *  dataType: {
     *    name: <type_for_mapping>
     *  },
     * }
     */
    geojsonCopyAttributes: {
      type: Array,
    },
    disableAttributesMapping: {
      type: Boolean,
      default: false,
    },
    formCols: {
      type: [Number, String],
      default: 12,
    },
    dialogTitle: {
      type: String,
    },
    layerTitle: {
      type: String,
    },
    hideLayerSubtitle: {
      type: Boolean,
      default: false,
    },
    mappingAttributesTitle: {
      type: String,
    },
    agreeText: {
      type: String,
      default: 'dialog.agreeCopy',
    },
    customTargetLayers: {
      // [{ value: 'layerId', text: 'layerLabel' }, ...]
      type: Array,
    },
    customIsValid: {
      type: Boolean,
      default: true,
    },
    customCopyObjectsAction: {
      type: Function,
    },
    appendLayerInputs: {
      type: [Array, Function],
      default: () => [],
    },
  },
  computed: {
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        if (!nV) {
          this.targetLayerId = null;
          this.isMapAttributes = false;
          this.mapAttributes = {};
        }
        this.$emit('update:isVisible', nV);
      },
    },
    computedIsLoading: {
      get() {
        return this.isLoading || false;
      },
      set(nV) {
        this.$emit('update:isLoading', nV);
      },
    },
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    inputDataSourceName() {
      return this.inputLayer?.data_source_name || '';
    },
    inputDataSource() {
      return this.dataSources?.[this.inputDataSourceName] || undefined;
    },
    inputDataSourceIdAttributeName() {
      return this.inputDataSource?.attributes_schema?.id_name || '';
    },
    inputDataSourceIdAttributeFilterName() {
      return this.inputDataSourceUseQualifiedNames
        ? this.inputDataSourceIdAttributeName
        : `${this.inputDataSourceName}.${this.inputDataSourceIdAttributeName}`;
    },
    inputDataSourceUseQualifiedNames() {
      return this.inputDataSource?.attributes_use_datasource_qualified_names || false;
    },
    inputDataSourceAttributes() {
      return this.inputDataSource?.attributes_schema?.attributes || [];
    },
    inputLayerAttributesSchema() {
      return this.inputLayer?.form_schema?.elements || [];
    },
    inputLayerAttributes() {
      return (
        this.geojsonCopyAttributes ||
        this.$_flattenStructure(this.getForm(this.inputLayerAttributesSchema, this.inputDataSourceAttributes))
      );
    },
    inputLayer() {
      return this.layers?.[this.inputLayerId] || undefined;
    },
    tableLid() {
      return this.$route.params.lid;
    },
    copyObjectsGeometryInputs() {
      return [
        {
          name: 'layerId',
          ...(this.layerTitle
            ? { straightTitle: this.layerTitle }
            : { translationPath: 'sidebar.copyObjectsGeometry.targetLayer' }),
          ...(this.hideLayerSubtitle ? {} : { subtitle: 'sidebar.copyObjectsGeometry.targetLayerSubtitle' }),
          dataType: { name: 'select' },
          items: this.targetLayers,
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          noDataText: this.$i18n.t('sidebar.copyObjectsGeometry.noLayersMatchingGeometryInProject'),
          readonly: this.computedIsLoading,
        },
        ...(typeof this.appendLayerInputs === 'function'
          ? this.appendLayerInputs({
              isLoading: this.computedIsLoading,
            })
          : this.appendLayerInputs),
        ...(this.disableAttributesMapping
          ? []
          : [
              {
                name: 'isMapAttributes',
                label: this.mappingAttributesTitle || this.$i18n.t('sidebar.copyObjects.mapAttributes'),
                dataType: { name: 'checkbox' },
                leftCol: 0,
                disabled: !this.targetLayerId,
                readonly: this.computedIsLoading,
              },
              ...(this.isMapAttributes
                ? this.targetLayerAttributesEditable.map(attribute => {
                    return {
                      name: attribute.attribute,
                      straightTitle: attribute.label,
                      dataType: { name: 'select' },
                      items: this.getInputLayerAttributesItems(attribute),
                      cols: '12',
                      clearable: true,
                      readonly: this.computedIsLoading,
                    };
                  })
                : []),
            ]),
      ];
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    targetLayers() {
      return (
        this.customTargetLayers ||
        this.projectLayers
          ?.filter(
            layer =>
              layer.layer_scope === 'core' &&
              !layer.cache &&
              (layer.geometry_type?.includes(
                this.geojson
                  ? this.geojson.features?.[0]?.geometry.type.toLocaleLowerCase()
                  : this.inputLayer?.geometry_type
              ) ||
                layer.geometry_type === null)
          )
          .map(layer => {
            const { id, name } = layer;
            return { value: id, text: name };
          })
      );
    },
    targetDataSourceName() {
      return this.targetLayer?.data_source_name || '';
    },
    targetDataSource() {
      return this.dataSources?.[this.targetDataSourceName] || undefined;
    },
    targetDataSourceAttributes() {
      return this.targetDataSource?.attributes_schema?.attributes || [];
    },
    targetLayer() {
      return this.layers?.[this.targetLayerId] || undefined;
    },
    targetLayerAttributesSchema() {
      return this.targetLayer?.form_schema?.elements || [];
    },
    targetLayerAttributes() {
      return this.$_flattenStructure(this.getForm(this.targetLayerAttributesSchema, this.targetDataSourceAttributes));
    },
    targetLayerAttributesEditable() {
      return this.targetLayerAttributes.filter(attribute => attribute.editable);
    },
  },
  data: () => ({
    isMapAttributes: false,
    targetLayerId: null,
    mapAttributes: {},
  }),
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    uploadDataSource: call('layers/uploadDataSource'),
    async copyObjectsHandler() {
      let mapAttributes = {};
      if (this.isMapAttributes)
        mapAttributes = Object.entries(this.mapAttributes).reduce((total, [targetAttribute, inputAttribute]) => {
          if (inputAttribute) total[targetAttribute] = inputAttribute;
          return total;
        }, {});
      this.computedIsLoading = true;
      try {
        let geojson;
        if (this.geojson) geojson = JSON.parse(JSON.stringify(this.geojson));
        else {
          const payload = {
            layer_id: this.inputLayerId,
            features_filter: {
              $IN: {
                [this.inputDataSourceIdAttributeFilterName]: this.inputFeaturesIds,
              },
            },
          };
          const r = await this.getLayerFeatures(payload);
          geojson = r.data.data.features;
        }
        let erroredAttributes = {};
        geojson.features = geojson.features.map(feature => {
          if (this.isMapAttributes) {
            feature.properties = Object.entries(mapAttributes).reduce((total, [targetAttribute, inputAttribute]) => {
              const tA = this.targetLayerAttributes.find(attribute => attribute.attribute === targetAttribute);
              if (tA?.items?.length) {
                const inputAttributeValue =
                  inputAttribute === this.inputDataSourceIdAttributeName
                    ? feature.id
                    : feature.properties[inputAttribute];
                if (tA.items.find(item => item === inputAttributeValue || item?.value === inputAttributeValue)) {
                  total[targetAttribute] = inputAttributeValue;
                } else {
                  if (!Object.keys(erroredAttributes).includes(targetAttribute)) {
                    erroredAttributes[targetAttribute] = {
                      value: targetAttribute,
                      text: tA.label,
                      count: 0,
                    };
                  }
                  if (inputAttributeValue || inputAttributeValue === 0 || inputAttributeValue === false)
                    erroredAttributes[targetAttribute].count++;
                }
              } else if (inputAttribute === this.inputDataSourceIdAttributeName) {
                total[targetAttribute] = feature.id;
              } else {
                total[targetAttribute] = feature.properties[inputAttribute];
              }
              return total;
            }, {});
          }
          if (!this.targetLayer.geometry_type) feature.geometry = null;
          delete feature.id;
          return feature;
        });
        if (this.customCopyObjectsAction) await this.customCopyObjectsAction(geojson, this.targetLayerId);
        else await this.copyObjects(geojson);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('sidebar.copyObjectsGeometry.successCopyFeatures'),
        });
        this.projectLayers.forEach(layer => {
          if (layer.data_source_name === this.targetDataSourceName) this.$root.$emit('refreshMvtSource', layer.id);
        });
        if (this.targetLayerId === this.inputLayerId || this.targetLayerId === this.tableLid)
          this.$root.$emit('refreshTableData');
        erroredAttributes = Object.entries(erroredAttributes).reduce((total, [key, value]) => {
          if (value.count) total[key] = value;
          return total;
        }, {});
        if (Object.keys(erroredAttributes).length) {
          const errorsMsg = Object.values(erroredAttributes)
            .map(eA => `"${eA.text}": ${eA.count} ${this.$i18n.tc('default.objects', eA.count)}`)
            .join(', ');
          this.$store.set('snackbar/PUSH_ERROR!', {
            message: `${this.$i18n.t('sidebar.copyObjects.mappingErrors')}: ${errorsMsg}`,
          });
        }
        const storageCopyData = JSON.parse(localStorage.getItem('copy-data') || '{}') || {};
        if (!storageCopyData[this.inputLayerId]) storageCopyData[this.inputLayerId] = {};
        storageCopyData[this.inputLayerId][this.targetLayerId] = mapAttributes;
        localStorage.setItem('copy-data', JSON.stringify(storageCopyData));

        if (this.name) {
          const storageTargetLayers = JSON.parse(localStorage.getItem('copy-data-last-layers') || '{}') || {};
          storageTargetLayers[this.name] = { layer: this.targetLayerId, mapping: this.isMapAttributes };
          localStorage.setItem('copy-data-last-layers', JSON.stringify(storageTargetLayers));
        }
        this.$emit('success');
        this.computedIsVisible = false;
      } finally {
        this.computedIsLoading = false;
      }
    },
    async copyObjects(geojson) {
      const blob = new Blob([JSON.stringify(geojson)], { type: 'application/geo+json;charset=utf-8;' });
      const file = new File([blob], 'copyFeatures.geojson');
      const formData = new FormData();
      formData.append('geojson', file);
      // formData.append('srid', geojson.crs.properties.name.split(':')[1]);
      formData.append('datasource_name', this.targetDataSourceName);
      await this.uploadDataSource(formData);
    },
    getInputLayerAttributesItems({ dataType: { name: targetDTName } = {} }) {
      const databoxAttrsMap = {
        'double precision': ['float', 'decimal'],
      };
      return this.inputLayerAttributes
        .filter(({ dataType: { name: inputAttributeDT } = {} }) => {
          return (databoxAttrsMap[inputAttributeDT] || [inputAttributeDT]).includes(targetDTName);
        })
        .map(attribute => {
          return { value: attribute.attribute, text: attribute.label };
        });
    },
  },
  watch: {
    computedIsVisible: {
      handler(nV) {
        if (nV && this.inputLayerId && this.name) {
          const storageTargetLayers = JSON.parse(localStorage.getItem('copy-data-last-layers') || '{}') || {};
          const storageTarget = storageTargetLayers[this.name] || null;
          if (storageTarget?.layer && this.targetLayers.find(l => l.value === storageTarget.layer)) {
            this.targetLayerId = storageTarget.layer;
            this.$nextTick(() => {
              this.isMapAttributes = !!storageTarget?.mapping;
            });
          }
        }
      },
      immediate: true,
    },
    targetLayerId: {
      handler() {
        this.isMapAttributes = false;
        this.mapAttributes = {};
      },
    },
    isMapAttributes: {
      handler(nV) {
        if (nV) {
          const storageCopyData = JSON.parse(localStorage.getItem('copy-data') || '{}') || {};
          if (storageCopyData[this.inputLayerId]?.[this.targetLayerId]) {
            this.mapAttributes = Object.entries(storageCopyData[this.inputLayerId][this.targetLayerId]).reduce(
              (total, [targetAttribute, inputAttribute]) => {
                if (this.targetLayerAttributesEditable.find(attribute => attribute.attribute === targetAttribute)) {
                  total[targetAttribute] = inputAttribute;
                }
                return total;
              },
              {}
            );
          } else if (this.targetLayerId === this.inputLayerId) {
            this.mapAttributes = this.targetLayerAttributesEditable.reduce((total, { attribute }) => {
              total[attribute] = attribute;
              return total;
            }, {});
          } else {
            const attributesWithSameNameAndDataTypeName = this.targetLayerAttributesEditable.filter(targetAttribute =>
              this.inputLayerAttributes.find(
                inputAttribute =>
                  inputAttribute.attribute === targetAttribute.attribute &&
                  inputAttribute.dataType.name === targetAttribute.dataType.name
              )
            );
            if (attributesWithSameNameAndDataTypeName.length) {
              this.mapAttributes = attributesWithSameNameAndDataTypeName.reduce((total, { attribute }) => {
                total[attribute] = attribute;
                return total;
              }, {});
            }
          }
        }
      },
    },
  },
};
</script>
